import { getContrastYIQ, getRandomPartialColor } from '#lib/helper/color.js';
import {
	applyOptimisticGiftcardConsumption,
	applyOptimisticGiftcardRecredit,
	applyOptimisticGiftcardStatsConsumption,
	applyOptimisticGiftcardStatsRecredit,
	markGiftcardLocallyMutated
} from '#lib/helper/giftcard.js';
import { generateUUID } from '#lib/helper/uuid.js';
import {
	GiftcardValidityMode,
	PaymentMethod,
	PaymentPosMethod,
	PaymentStatus,
	type BookableGiftcard,
	type ConsumeGiftcardRequest,
	type CreateGiftcardRequest,
	type Customer,
	type Giftcard,
	type GiftcardStats,
	type Payment,
	type RecreditGiftcardRequest,
	type SaveBookableGiftcardRequest,
	apiAppGiftcardGiftcardConsumegiftcard,
	apiAppGiftcardGiftcardCreategiftcard,
	apiAppGiftcardGiftcardDeletegiftcard,
	apiAppGiftcardGiftcardGetgiftcards,
	apiAppGiftcardGiftcardRecreditgiftcard,
	apiAppGiftcardGiftcardResendemail,
	apiAppGiftcardGiftcardUpdategiftcard,
	apiAppGiftcardGiftcardbookableDeletebookablegiftcard,
	apiAppGiftcardGiftcardbookableNewbookablegiftcard
} from '#lib/types/generated/index.js';
import { optimisticDelete, optimisticSave } from '#lib/stores/optimistic.js';
import { get, writable } from 'svelte/store';
import { bookingStore } from './booking';
import { addCustomerToStore } from './customer';
import { CurrencyCode, formatCurrency } from '#lib/helper/currency.js';
import { t } from './i18n';
import { infinityListReloadStore } from './infinity-list';
import { now } from './time';
import { toastError, toastSuccess } from './toast';

/**
 * Giftcard Store
 */
export const giftcardStore = writable<Giftcard[]>([]);

export function setGiftcards(cards: Giftcard[]): void {
	const customers: Customer[] = [];
	for (const card of cards) {
		if (card.customer) customers.push(card.customer);
		if (card.buyer) customers.push(card.buyer);
	}
	if (customers.length > 0) {
		addCustomerToStore(customers);
	}
	giftcardStore.set(cards);
}

export function upsertGiftcard(giftcard: Giftcard | Giftcard[]): void {
	const items = Array.isArray(giftcard) ? giftcard : [giftcard];
	if (items.length === 0) return;

	const customers: Customer[] = [];
	for (const card of items) {
		if (card.customer) customers.push(card.customer);
		if (card.buyer) customers.push(card.buyer);
	}
	if (customers.length > 0) {
		addCustomerToStore(customers);
	}

	const newIds = new Set(items.map((g) => g.id.trim()));
	giftcardStore.update((current) =>
		current.filter((g) => !newIds.has(g.id.trim())).concat(items)
	);
}

export const bookableGiftcardStore = writable<BookableGiftcard[]>([]);
export const bookableGiftcardSelectedStore = writable<BookableGiftcard | undefined>(undefined);
export const defaultGiftcard: BookableGiftcard = {
	id: '',
	realPrice: 50,
	cardColor: '#ffffff',
	textColor: '#000000',
	onlinePrice: 50,
	label: '',
	description: '',
	publishStart: null,
	publishEnd: null,
	validityDate: null,
	validityPeriod: 365,
	validityMode: GiftcardValidityMode.Period,
	icon: null,
	logo: null,
	backgroundOverlayDisplay: false,
	backgroundImage: null
};

/**
 * Giftcard Stats Store
 */
export const giftcardStatsStore = writable<GiftcardStats>({
	sold: 0,
	income: 0,
	remaining: 0
});

export function giftcardSearch(
	params: { code?: string; id?: string } | string
): Promise<Giftcard | undefined> {
	const body: Record<string, any> = {
		include_expired: true,
		exclude_empty: false,
		include_archived: true
	};
	if (typeof params === 'string') {
		body.code = params.replace(/^#/, '').trim();
	} else {
		if (params.code) body.code = params.code.replace(/^#/, '').trim();
		if (params.id) body.id = params.id;
	}

	return apiAppGiftcardGiftcardGetgiftcards(body).then((res) => {
		const items = res.items;
		if (items.length === 0) {
			return undefined;
		}
		const customer = items[0].customer;
		if (customer) addCustomerToStore(customer);
		return items[0];
	});
}

export function giftcardConsume(
	giftcard: Giftcard,
	amount: number,
	bookingId?: string,
	paymentId?: string
): Promise<Payment> {
	// Snapshot for rollback
	const previousGiftcardStore = get(giftcardStore);
	const previousBookingStore = get(bookingStore);
	const previousStatsStore = get(giftcardStatsStore);

	markGiftcardLocallyMutated(giftcard.id);

	// Optimistic update on giftcard
	giftcardStore.update((cards) =>
		cards.map((g) => (g.id === giftcard.id ? applyOptimisticGiftcardConsumption(g, amount) : g))
	);

	// Optimistic update on giftcard stats
	giftcardStatsStore.update((stats) => applyOptimisticGiftcardStatsConsumption(stats, amount));

	// Optimistic update on booking payments
	const resolvedPaymentId = paymentId ?? generateUUID();
	let payment: Payment | null = null;
	if (bookingId) {
		payment = {
			id: resolvedPaymentId,
			bookingId: bookingId,
			amount: amount,
			bookingAmount: amount,
			bookingAmountRefunded: 0,
			amountRefunded: 0,
			value: 0,
			fee: 0,
			net: amount,
			splitFee: 0,
			ownerSplitFee: 0,
			method: PaymentMethod.Giftcard,
			posMethod: null,
			status: PaymentStatus.Success,
			createdAt: get(now),
			updatedAt: get(now),
			bookingDate: null,
			parentPaymentId: null,
			parentPayment: null,
			objectId: giftcard.code,
			errorCode: null,
			customer: null
		};
		bookingStore.update((bookings) =>
			bookings.map((b) =>
				b.id === bookingId ? { ...b, payments: [...b.payments, payment!] } : b
			)
		);
	}

	const body: ConsumeGiftcardRequest = {
		amount: amount,
		bookingId: bookingId ?? null,
		id: resolvedPaymentId
	};

	return apiAppGiftcardGiftcardConsumegiftcard(giftcard.id, body)
		.then((apiPayment) => {
			if (bookingId && payment) {
				bookingStore.update((bookings) =>
					bookings.map((b) =>
						b.id === bookingId
							? {
									...b,
									payments: b.payments.map((p) =>
										p.id === payment!.id
											? {
													...p,
													...apiPayment,
													objectId:
														apiPayment.objectId ||
														giftcard.code ||
														giftcard.id
												}
											: p
									)
								}
							: b
					)
				);
			}
			infinityListReloadStore.update((s) => ({ ...s, [`giftcard_${giftcard.id}`]: true }));
			toastSuccess(
				get(t)('giftcard.consume.consumed', {
					amount: formatCurrency(giftcard.remainingAmount - amount, CurrencyCode.EUR)
				})
			);
			return apiPayment;
		})
		.catch((err) => {
			// Rollback on error
			giftcardStore.set(previousGiftcardStore);
			giftcardStatsStore.set(previousStatsStore);
			if (bookingId) {
				bookingStore.set(previousBookingStore);
			}
			toastError(get(t)('giftcard.consume.error'));
			throw err;
		});
}

export function giftcardRecredit(
	giftcard: Giftcard,
	amount: number,
	bookingId?: string,
	paymentId?: string
): Promise<Payment> {
	// Snapshot for rollback
	const previousGiftcardStore = get(giftcardStore);
	const previousBookingStore = get(bookingStore);
	const previousStatsStore = get(giftcardStatsStore);

	markGiftcardLocallyMutated(giftcard.id);

	// Optimistic update on giftcard
	giftcardStore.update((cards) =>
		cards.map((g) => (g.id === giftcard.id ? applyOptimisticGiftcardRecredit(g, amount) : g))
	);

	// Optimistic update on giftcard stats
	giftcardStatsStore.update((stats) => applyOptimisticGiftcardStatsRecredit(stats, amount));

	// Optimistic update on booking payments (refund entry)
	const resolvedPaymentId = paymentId ?? generateUUID();
	const refundPayment: Payment = {
		id: resolvedPaymentId,
		bookingId: bookingId ?? null,
		amount: -amount,
		bookingAmount: -amount,
		bookingAmountRefunded: 0,
		amountRefunded: 0,
		value: 0,
		fee: 0,
		net: -amount,
		splitFee: 0,
		ownerSplitFee: 0,
		method: PaymentMethod.Giftcard,
		posMethod: null,
		status: PaymentStatus.Success,
		createdAt: get(now),
		updatedAt: get(now),
		bookingDate: null,
		parentPaymentId: null,
		parentPayment: null,
		objectId: giftcard.code,
		errorCode: null,
		customer: null
	};
	if (bookingId) {
		bookingStore.update((bookings) =>
			bookings.map((b) =>
				b.id === bookingId ? { ...b, payments: [...b.payments, refundPayment] } : b
			)
		);
	}

	const body: RecreditGiftcardRequest = {
		amount: amount,
		bookingId: bookingId ?? null,
		id: resolvedPaymentId
	};

	return apiAppGiftcardGiftcardRecreditgiftcard(giftcard.id, body)
		.then((apiPayment) => {
			if (bookingId) {
				bookingStore.update((bookings) =>
					bookings.map((b) =>
						b.id === bookingId
							? {
									...b,
									payments: b.payments.map((p) =>
										p.id === refundPayment.id
											? {
													...p,
													...apiPayment,
													objectId:
														apiPayment.objectId ||
														giftcard.code ||
														giftcard.id
												}
											: p
									)
								}
							: b
					)
				);
			}
			infinityListReloadStore.update((s) => ({ ...s, [`giftcard_${giftcard.id}`]: true }));
			toastSuccess(get(t)('giftcard.save.success'));
			return apiPayment;
		})
		.catch((err) => {
			// Rollback on error
			giftcardStore.set(previousGiftcardStore);
			giftcardStatsStore.set(previousStatsStore);
			if (bookingId) {
				bookingStore.set(previousBookingStore);
			}
			toastError(get(t)('giftcard.save.error'));
			throw err;
		});
}

export function generateNewGiftcard(): BookableGiftcard {
	const color = getRandomPartialColor();
	return {
		id: generateUUID(),
		realPrice: 50,
		onlinePrice: 50,
		cardColor: color,
		textColor: getContrastYIQ(color),
		label: '',
		description: '',
		publishStart: null,
		publishEnd: null,
		validityMode: GiftcardValidityMode.Period,
		validityPeriod: 365,
		validityDate: get(now),
		icon: 'Gift',
		logo: null,
		backgroundOverlayDisplay: false,
		backgroundImage: null
	};
}

export function saveBookableGiftcard(giftcard: BookableGiftcard): Promise<BookableGiftcard> {
	const payload: SaveBookableGiftcardRequest = {
		...giftcard,
		publishStart: giftcard.publishStart?.toISO() ?? null,
		publishEnd: giftcard.publishEnd?.toISO() ?? null,
		validityDate: giftcard.validityDate?.toISODate() ?? null
	};
	return optimisticSave(bookableGiftcardStore, giftcard, () =>
		apiAppGiftcardGiftcardbookableNewbookablegiftcard(payload)
	);
}

export function deleteBookableGiftcard(giftcard: BookableGiftcard): Promise<void> {
	return optimisticDelete(bookableGiftcardStore, giftcard, () =>
		apiAppGiftcardGiftcardbookableDeletebookablegiftcard(giftcard.id)
	);
}

export function updateGiftcard(giftcard: Giftcard): Promise<Giftcard> {
	if (giftcard.customer) {
		addCustomerToStore(giftcard.customer);
	}
	return optimisticSave(giftcardStore, giftcard, () =>
		apiAppGiftcardGiftcardUpdategiftcard(giftcard.id, {
			customerId: giftcard.customerId ?? null,
			memo: giftcard.memo ?? null,
			expiresAt: giftcard.expiresAt.toISO()
		})
	);
}

export function resendGiftcardEmail(giftcard: Giftcard): Promise<void> {
	return apiAppGiftcardGiftcardResendemail(giftcard.id);
}

export async function createGiftcard(
	gcPayload: CreateGiftcardRequest
): Promise<Giftcard | undefined> {
	await apiAppGiftcardGiftcardCreategiftcard(gcPayload);
	bookableGiftcardSelectedStore.set(undefined);
	if (!gcPayload.id) return undefined;
	const res = await apiAppGiftcardGiftcardGetgiftcards({
		id: gcPayload.id
	});
	const items = res.items;
	const gcs = items.find((g) => g.id == gcPayload.id);
	if (gcs) {
		if (gcs.customer) {
			addCustomerToStore(gcs.customer);
		}
		giftcardStore.update((cards) => [...cards, gcs]);
		return gcs;
	}
	return undefined;
}

export async function deleteOrCloseGiftcard(
	id: string,
	options: {
		action: 'delete' | 'close';
		refundAmount?: number;
		refundMethod?: PaymentMethod | null;
		refundPosMethod?: PaymentPosMethod | null;
	}
): Promise<void> {
	await apiAppGiftcardGiftcardDeletegiftcard(id, options);
	giftcardStore.update((cards) => cards.filter((c) => c.id !== id));
}
